/**
 * @module Service
 */

import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { UserService } from './user.service';
import { filter, first } from 'rxjs/operators';
import { Auth0UserVerification, User } from '@feature/users/shared';
import { SnackBarService } from '@app/services/snackbar.service';
import { AuthService } from '@app/services/auth.service';

/**
 * Service for managing user account verification processes within the application.
 *
 * This service handles the verification state of users authenticated through Auth0. It provides
 * observables to track the verification status and refreshes the user verification state as needed.
 * It also manages interactions with local storage to persist verification data.
 *
 * @service
 * @@Injectable
 */
@Injectable({ providedIn: 'root' })
export class UserVerificationService implements OnDestroy {
	/**
	 * The user object representing the currently verified user.
	 * @type {User}
	 */
	private _user: User;

	/**
	 * BehaviorSubject that holds the verification status of the Auth0 user.
	 *
	 * This observable emits the current verification state and allows subscribers to react to changes.
	 *
	 * @type {BehaviorSubject<Auth0UserVerification>}
	 * @see [Data Sharing in a Multi-View Application](https://www.lucidchart.com/techblog/2016/11/08/angular-2-and-observables-data-sharing-in-a-multi-view-application/)
	 */
	public verifiedUser$: BehaviorSubject<Auth0UserVerification>;

	/**
	 * Subscription that holds the Observable for tracking the API user.
	 * @type {Subscription}
	 */
	private auth0User$: Subscription;

	/**
	 * Interval ID for checking the verification state in local storage.
	 * @type {number}
	 */
	private _verificationInterval: number;

	/**
	 * Creates an instance of the UserVerificationService.
	 *
	 * @constructor
	 * @param {AuthService} _authService - The authentication service used for managing user sessions.
	 * @param {UserService} _userService - The service responsible for managing user-related operations.
	 * @param {SnackBarService} _snackbarService - Service for displaying snack bar messages.
	 * @param {NgZone} _ngZone - Angular NgZone service for managing change detection.
	 */
	constructor(
        private _authService: AuthService,
        private _userService: UserService,
		private _snackbarService: SnackBarService,
        private _ngZone: NgZone
	) {
		this.verifiedUser$ = new BehaviorSubject<Auth0UserVerification>(null);

		const verifiedUserStr = localStorage.getItem('verifiedUser');
		let verifiedUser: Auth0UserVerification = null;

		if (verifiedUserStr !== null) { // If verified user found, use it.
			verifiedUser = Object.assign(new Auth0UserVerification(), JSON.parse(verifiedUserStr));
			this.verifiedUser$.next(verifiedUser);
		} else { // Call an API to get fresh state for the Auth0 user.
			this.refreshUserVerification();
		}

		// Start the interval for localStorage checking.
		this.startListeningToLocalStorage(5000);
	}

	/**
     * Start listening to localStorage changes.
     *
	 * @param {number} frequency
	 *
     * @see [NgZone](https://docs.angular.lat/guide/zone)
     * @see [Reduce change detection calls](https://medium.com/@krzysztof.grzybek89/how-runoutsideangular-might-reduce-change-detection-calls-in-your-app-6b4dab6e374d)
     */
	public startListeningToLocalStorage(frequency: number = 1000): void {
		// By default, all asynchronous operations are inside the Angular zone, which triggers change detection automatically.
		// You know no data will be updated, so you don't want to trigger change detection
		// in this specified operation. Instead, call ngZone.runOutsideAngular().
		// @note Running the interval outside Angular ensures that e2e tests will not hang.
		this._ngZone.runOutsideAngular(() => {
			// Track the updates but don't trigger change detection.
			this._verificationInterval = window.setInterval(() => {
				this.getUserVerification()
					.pipe(filter((auth0UserVer: Auth0UserVerification) => auth0UserVer !== null))
					.subscribe((auth0UserVer: Auth0UserVerification) => {
						if (auth0UserVer.emailVerified) {
							window.clearInterval(this._verificationInterval);
						}
					});
			}, frequency);
		});
	}

	/**
	 * Refreshes the user verification.
	 *
	 * @returns {Observable<Auth0UserVerification>}
	 */
	public refreshUserVerification(): Observable<Auth0UserVerification> {
		this.setUserVerification();
		return this.getUserVerification();
	}

	/**
	 * Gets the user verification.
	 *
	 * @returns {Observable<Auth0UserVerification>}
	 */
	public getUserVerification(): Observable<Auth0UserVerification> {
		return this.verifiedUser$.asObservable();
	}

	/**
	 * Sets the user verification.
	 */
	public setUserVerification(): void {
		// LocalStorage has to be cleared in order to be recognized again during service init.
		// At first, it seemed to be because of the browser Cookies but appeared to be in clearing.
		localStorage.removeItem('verifiedUser');

		// Get the current user.
		this.auth0User$ = this._authService.apiUser$
			.pipe(first(user => user !== null))
			.subscribe((user: User) => {
				// Check that the current user's Auth0 email is verified.
				const auth0EmailVerification$ = this._userService.isEmailVerified(user?.id)
					.subscribe({
						next: (auth0UserVerification: Auth0UserVerification) => {
							localStorage.setItem('verifiedUser', JSON.stringify(auth0UserVerification));
							this.verifiedUser$.next(auth0UserVerification);
						},
						error: (error: any) => this._snackbarService.error('Error occurred while checking the email verification: ' + error)
					});
			});
	}

	/**
	 * Unsubscribe from all subscriptions when the component is destroyed.
	 */
	ngOnDestroy() {
		this._user = null;
		this.auth0User$.unsubscribe();
		window.clearInterval(this._verificationInterval);
	}
}
